import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { IsoDateSchema, MessageSchema, UnitPrecisionSchema } from './primitives.js';
const c = initContract();
/**
 * PART 14 — the four dashboards (DSH-01..DSH-04) and the shared board engine
 * (DSH-00).
 *
 * Layouts: one saved layout per user per board (owner ruling R1 /
 * BR-DSH-00-04), stored against the user so it follows them (BR-DSH-00-03).
 * The record is the spec's own `[{id, size, visible, order}]`.
 *
 * Data: one endpoint per widget cluster (financial / sales / inventory /
 * cashflow / forecast) — BR-DSH-00-13's "one filter change re-reads every
 * widget" maps to one round trip. Nothing here writes books (BR-DSH-00-01).
 *
 * Permissions (PART 14 §14.0 + BR-DSH-00-17): every route needs
 * `reports.view` (the Home group's gate); each cluster additionally requires
 * the area whose figures it shows, enforced in the service — sales.view,
 * inventory.view, banking.view (cash), accounting.view (DSH-01). The
 * payroll fence (BR-PERM-40): the cash outflow category "Payroll" exists
 * only for callers holding payroll.view; for everybody else the figure is
 * folded into "Other payments" and NO payroll-labelled number leaves the
 * server.
 */
// ── Layouts (DSH-00 §14.1.7) ────────────────────────────────────────────────
export const DASHBOARD_BOARDS = ['sales', 'inventory', 'cashflow'];
export const DashboardBoardSchema = z.enum(DASHBOARD_BOARDS);
export const WidgetSizeSchema = z.enum(['sm', 'md', 'lg']);
export const LayoutEntrySchema = z
    .object({
    id: z.string().min(1).max(60),
    size: WidgetSizeSchema,
    visible: z.boolean(),
    order: z.number().int().min(0).max(999),
})
    .strict();
export const LayoutSchema = z.array(LayoutEntrySchema).max(40);
// ── Shared widget shapes ────────────────────────────────────────────────────
/** Signed decimal string — dashboard figures may be negative (net cash flow). */
const SignedMoney = z.string().regex(/^-?\d{1,13}\.\d{2}$/);
/** One ranked-bars row (DSH-02 §5j / DSH-03 §5h / DSH-04 §5g). */
const RankedRowSchema = z
    .object({ id: z.string().nullable(), name: z.string(), value: SignedMoney })
    .strict();
/** One trend bucket. */
const TrendBucketSchema = z
    .object({ label: z.string(), inflow: SignedMoney, outflow: SignedMoney })
    .strict();
// ── DSH-01 Financial Dashboard (fixed) ──────────────────────────────────────
const KpiSchema = z
    .object({
    value: SignedMoney,
    /** Signed, 1 decimal, null when the previous period is zero (BR-DSH-02-07). */
    changePct: z.string().nullable(),
})
    .strict();
export const FinancialDashboardSchema = z
    .object({
    /** BR-DSH-01-02: netProfit = revenue − expenses, comparison = previous quarter. */
    totalRevenue: KpiSchema,
    totalExpenses: KpiSchema,
    netProfit: KpiSchema,
    cashBalance: KpiSchema,
    /** BR-DSH-01-05: exactly 6 periods of the current half-year. */
    chart: z.array(z.object({ label: z.string(), income: SignedMoney, expenses: SignedMoney }).strict()),
    /**
     * BR-DSH-01-07: exactly FIVE bands summing to total outstanding.
     *
     * DECISION 5 (delegated rulings, 26 Aug 2026) — "aged-debt buckets → five
     * everywhere. The statement already resolved to five; one number must not
     * disagree with itself one click away." This panel carried F-13's four
     * (current / 1–30 / 31–60 / 60+) while CRM-04's statement carried §21.8's
     * five, so a receivable 75 days late was "over 60" on the dashboard and
     * "61–90" one click away, and no screen said the two were the same money.
     *
     * THE KEYS ARE `AgingBand`'s, NOT A FIFTH SPELLING. They were `days1to30`
     * / `days31to60` / `days60plus` — a vocabulary this panel used and nothing
     * else did. Naming them after the shared band list is what lets the
     * browser iterate `AGING_BANDS` and label each one with the statement's
     * OWN dictionary entry (`partners.stBand_*`), so the two screens cannot
     * drift in their words either. The band boundaries are shared the same
     * way: `AGING_BAND_MAX_DAYS_PAST_DUE`, which the server's SQL interpolates
     * rather than retyping 30/60/90.
     *
     * BR-DSH-01-07's own sentence said "exactly four bands" and moves to five
     * under this ruling; the spec edit ships beside the build.
     */
    arAging: z
        .object({
        current: SignedMoney,
        d1_30: SignedMoney,
        d31_60: SignedMoney,
        d61_90: SignedMoney,
        d90_plus: SignedMoney,
    })
        .strict(),
    /** BR-DSH-01-10: exactly 5 posted journal lines, newest first. */
    recent: z.array(z
        .object({
        date: IsoDateSchema,
        description: z.string(),
        account: z.string(),
        amount: SignedMoney,
        direction: z.enum(['in', 'out']),
    })
        .strict()),
    /**
     * THE CONSOLIDATION ON DSH-01 (F-27, GEN-115/-116) — the same three
     * fields DSH-04 carries, for the same reason and by the same rules.
     *
     * DSH-01 has no consolidation rule of its own: BR-DSH-01-01…-12 say
     * nothing about currency. What governs is therefore the general pair,
     * and both are unconditional. F-27: "never silently add different
     * currencies … the screen says so, naming the rate date." GEN-115: "ANY
     * screen that converts money between currencies must show a one-line
     * disclosure … naming the target currency and the date of the rates
     * used." So this screen needs no second convention, and must not invent
     * one — `Consolidation` is the seam, unchanged.
     *
     * `rateDate` is GEN-116's switch, decided on the server: the effective
     * date of the newest rate ACTUALLY applied, and null exactly when
     * nothing was converted. BR-DSH-04-11's second clause is why it is a
     * RATE's date rather than the as-of date.
     *
     * NOT every figure here is stated at that rate, and that is deliberate:
     * `arAging` reads `unpaid_base`, which holds a receivable at the rate
     * its invoice FROZE (GEN-111/-113). That is the books' reading and it is
     * correct; BR-DSH-04-05 licenses the disagreement — "neither may be
     * adjusted to match the other."
     */
    baseCurrency: z.string(),
    rateDate: IsoDateSchema.nullable(),
    /** ERR-DSH-04-04's sentence, once per currency that had no rate. */
    conversionNotes: z.array(z.string()),
})
    .strict();
// ── DSH-02 Sales Dashboard cluster ──────────────────────────────────────────
export const SalesWidgetsSchema = z
    .object({
    /**
     * BR-DSH-02-01: "Every money widget on this dashboard must show figures in
     * the company's primary currency. Documents in another currency must be
     * converted using the exchange rate stored on the document." Every money
     * field below is already in `baseCurrency` when it leaves the server —
     * GEN-107 puts the code here so the browser stops assuming the dollar.
     *
     * `conversionNote` is GEN-115 and GEN-116 in one value, decided on the
     * server because only the server knows whether a conversion happened.
     * There is no single rate DATE to name — each document carries the rate it
     * froze (GEN-111) — so it names the BASIS, which is CRM-04's answer to the
     * same problem (`FX_MESSAGES.convertedAtFrozenRates`). Null means nothing
     * converted, and the screen shows nothing.
     */
    baseCurrency: z.string(),
    conversionNote: z.string().nullable(),
    /** BR-DSH-02-03: POSTED documents only (R9). */
    totalSales: KpiSchema,
    /** BR-DSH-02-04: always today, whatever the filter. */
    totalToday: SignedMoney,
    /** BR-DSH-02-08. */
    openQuotations: z.object({ count: z.number().int(), total: SignedMoney }).strict(),
    /** BR-DSH-02-05: a position, ignores the date filter. */
    outstandingReceivables: SignedMoney,
    /** BR-DSH-02-09/-10. */
    averageOrderValue: SignedMoney,
    /** BR-DSH-02-20: buckets sum to the printed total. */
    trend: z.object({
        total: SignedMoney,
        buckets: z.array(z.object({ label: z.string(), value: SignedMoney }).strict()),
    }),
    byProduct: z.array(RankedRowSchema),
    byCustomer: z.array(RankedRowSchema),
    bySalesperson: z.array(RankedRowSchema),
    /** BR-DSH-02-11/-12: unpaid remainder, counted from the due date. */
    overdueInvoices: z.array(z
        .object({
        invoiceId: z.uuid(),
        customer: z.string(),
        invoiceNo: z.string(),
        unpaid: SignedMoney,
        daysOverdue: z.number().int(),
    })
        .strict()),
    recentQuotations: z.array(z
        .object({
        quotationId: z.uuid(),
        customer: z.string(),
        quoteNo: z.string(),
        total: SignedMoney,
        /**
         * BR-DSH-02-16 / §5l name four chips; SAL-01 §7 defines SIX statuses
         * and GEN-66 makes the DOCUMENT's own status the one a chip draws.
         * The two extra values are here because collapsing them said
         * something FALSE rather than something vague: `rejected` folded
         * into `expired` reported "no answer" about a customer who said no,
         * and `converted` fell through to `draft` — "still being written" —
         * about a quotation that had already become an invoice.
         */
        status: z.enum(['draft', 'sent', 'accepted', 'rejected', 'expired', 'converted']),
    })
        .strict()),
})
    .strict();
// ── DSH-03 Inventory Dashboard cluster ──────────────────────────────────────
export const InventoryWidgetsSchema = z
    .object({
    /** BR-DSH-03-01/-02. */
    lowStockCount: z.number().int(),
    outOfStockCount: z.number().int(),
    /** BR-DSH-03-06: Σ qty × average cost — money, never units (BR-DSH-03-19). */
    totalStockValue: SignedMoney,
    skusTracked: z.number().int(),
    pendingReceiptsCount: z.number().int(),
    movementTrend: z.object({
        inTotal: SignedMoney,
        outTotal: SignedMoney,
        buckets: z.array(TrendBucketSchema),
    }),
    valueByCategory: z.array(RankedRowSchema),
    /**
     * §1.5.3 / BR-DSH-00-18: null — never computed, never sent — when the
     * company does not hold `multi_branch`. The same shape the permission
     * fences below use (`upcomingReceivables` et al), for the same reason: a
     * widget "hidden entirely" that still ships its numbers to the browser is
     * hidden in the browser only, and the browser is never the control.
     */
    valueByWarehouse: z.array(RankedRowSchema).nullable(),
    lowStock: z.array(z
        .object({
        itemId: z.uuid(),
        name: z.string(),
        sku: z.string(),
        qty: z.string(),
        reorderLevel: z.string(),
        /** BR-DSH-03-09/-10: on-order subtracted, never negative (row dropped). */
        suggested: z.string(),
        /**
         * `N-4` — the item's stock unit and the decimals it allows. DSH-03's
         * tables are `N-14`'s own example: "items are counted in pieces,
         * kilograms, litres, metres and boxes", so a widget column of bare
         * numbers can be true of some rows only, and is an invitation to add
         * up what must never be added.
         */
        unitSymbol: z.string(),
        unitPrecision: UnitPrecisionSchema,
    })
        .strict()),
    outOfStock: z.array(z
        .object({
        itemId: z.uuid(),
        name: z.string(),
        sku: z.string(),
        daysOut: z.number().int(),
        lastRestock: IsoDateSchema.nullable(),
    })
        .strict()),
    deadStock: z.array(z
        .object({
        itemId: z.uuid(),
        name: z.string(),
        sku: z.string(),
        daysIdle: z.number().int(),
        qty: z.string(),
        /** `N-4` — dead stock is still stock, and it is measured in something. */
        unitSymbol: z.string(),
        unitPrecision: UnitPrecisionSchema,
    })
        .strict()),
    pendingReceipts: z.array(z
        .object({
        item: z.string(),
        reference: z.string(),
        expectedQty: z.string(),
        /** `N-4` — a PO line has no unit of its own; this is the item's stock unit. */
        unitSymbol: z.string(),
        unitPrecision: UnitPrecisionSchema,
        expectedDate: IsoDateSchema.nullable(),
        supplier: z.string(),
    })
        .strict()),
    recentAdjustments: z.array(z
        .object({
        item: z.string(),
        /**
         * The SIGNED quantity as stored — the `+` is composed on the screen,
         * beside the figure that is actually printed, so that a value which
         * rounds away to nothing cannot acquire one.
         */
        change: z.string(),
        /** `N-4` — a voucher line moves the item's stock unit (INV-08 §5.4). */
        unitSymbol: z.string(),
        unitPrecision: UnitPrecisionSchema,
        reason: z.string(),
        user: z.string(),
        when: z.string(),
    })
        .strict()),
})
    .strict();
// ── DSH-04 Cash Flow cluster ────────────────────────────────────────────────
const DueRowSchema = z
    .object({
    documentId: z.uuid(),
    party: z.string(),
    documentNo: z.string(),
    amount: SignedMoney,
    due: IsoDateSchema,
})
    .strict();
export const CashflowWidgetsSchema = z
    .object({
    /** BR-DSH-04-06: net = in − out; BR-DSH-04-01: cash basis, posted only. */
    currentCashBalance: SignedMoney,
    cashIn: SignedMoney,
    cashOut: SignedMoney,
    netCashFlow: SignedMoney,
    asOf: z.string(),
    /**
     * THE CONSOLIDATION, on the wire (BR-DSH-04-09/-10/-11, ERR-DSH-04-04).
     *
     * Every money figure in this response is stated in `baseCurrency` —
     * the COMPANY's own reporting currency, read from the currency table.
     * The screen used to print the literal 'USD' beside figures the server
     * had not converted at all; both halves of that are gone.
     *
     * `rateDate` is GEN-116's switch, decided here rather than in the
     * browser: it is the effective date of the newest rate ACTUALLY applied,
     * and null exactly when nothing was converted — a single-currency
     * company, or a view filtered to one currency (BR-DSH-04-32). The screen
     * shows the disclosure if and only if it is non-null, which is
     * BR-DSH-04-09 word for word. BR-DSH-04-11's second clause is why it is
     * a RATE's date and not the as-of date.
     */
    baseCurrency: z.string(),
    rateDate: IsoDateSchema.nullable(),
    /** ERR-DSH-04-04's sentence, once per currency that had no rate. */
    conversionNotes: z.array(z.string()),
    /** §6's currency filter choices — the base first, then what is held. */
    currencies: z.array(z.string()),
    /** BR-DSH-04-22: rows sum to currentCashBalance on All accounts. */
    byBankAccount: z.array(RankedRowSchema),
    /**
     * DSH-04 §5h. The Payroll row exists ONLY for callers holding
     * payroll.view (BR-PERM-40); otherwise its money is folded into
     * "Other payments" with no payroll-labelled figure anywhere.
     */
    categories: z.object({
        inflows: z.array(z.object({ key: z.string(), value: SignedMoney }).strict()),
        outflows: z.array(z.object({ key: z.string(), value: SignedMoney }).strict()),
    }),
    /** §5i: the 7-day both-directions week list. */
    upcomingWeek: z.array(z
        .object({
        party: z.string(),
        type: z.enum(['receivable', 'payable']),
        amount: SignedMoney,
        due: IsoDateSchema,
    })
        .strict()),
    /** Omitted (null) without sales.view — BR-DSH-00-17. */
    upcomingReceivables: z.array(DueRowSchema).nullable(),
    /** Omitted (null) without purchasing.view. */
    upcomingPayables: z.array(DueRowSchema).nullable(),
    overdueReceivables: z
        .array(z
        .object({
        documentId: z.uuid(),
        party: z.string(),
        documentNo: z.string(),
        amount: SignedMoney,
        daysOverdue: z.number().int(),
    })
        .strict())
        .nullable(),
    /** BR-DSH-04-28: uncleared, due inside the range. */
    checksDue: z.array(z
        .object({
        checkId: z.uuid(),
        checkNo: z.string(),
        direction: z.enum(['incoming', 'outgoing']),
        state: z.string(),
        party: z.string(),
        amount: SignedMoney,
        due: IsoDateSchema,
    })
        .strict()),
    trend: z.object({
        inTotal: SignedMoney,
        outTotal: SignedMoney,
        buckets: z.array(z
            .object({ label: z.string(), inflow: SignedMoney, outflow: SignedMoney, net: SignedMoney })
            .strict()),
    }),
})
    .strict();
/** DSH-04 §5e — the 30-day walk (BR-DSH-04-13/-14/-15/-16). */
export const CashflowForecastSchema = z
    .object({
    today: SignedMoney,
    projectedEnd: SignedMoney,
    /** BR-DSH-04-18: red when any of the 31 points dips below zero. */
    belowZero: z.boolean(),
    points: z.array(z.object({ date: IsoDateSchema, balance: SignedMoney }).strict()),
})
    .strict();
// ── The contract ────────────────────────────────────────────────────────────
const DateRangeQuery = z.object({
    from: IsoDateSchema.optional(),
    to: IsoDateSchema.optional(),
});
/**
 * DSH-04 §6's currency filter, and BR-DSH-04-32's whole contract: "When the
 * currency filter names a SINGLE currency, only accounts and documents in
 * that currency may be included, and NO CONVERSION may take place."
 *
 * Absent is the delivered default — §9's "every filter starts at 'all' or
 * CONSOLIDATED" — so omitting it is the consolidated view, not a missing
 * value to be defaulted somewhere else.
 */
const CurrencyFilterSchema = z
    .string()
    .regex(/^[A-Z]{3}$/)
    .optional();
export const dashboardsContract = c.router({
    getLayout: {
        method: 'GET',
        path: '/api/dashboards/:board/layout',
        pathParams: z.object({ board: DashboardBoardSchema }),
        responses: { 200: z.object({ layout: LayoutSchema }).strict() },
        summary: 'This user’s saved layout for one board — [] means the delivered default',
    },
    putLayout: {
        method: 'PUT',
        path: '/api/dashboards/:board/layout',
        pathParams: z.object({ board: DashboardBoardSchema }),
        body: z.object({ layout: LayoutSchema }).strict(),
        responses: { 200: z.object({ layout: LayoutSchema }).strict(), 400: MessageSchema },
        summary: 'Save this user’s one layout for one board (R1 / BR-DSH-00-04)',
    },
    resetLayout: {
        method: 'DELETE',
        path: '/api/dashboards/:board/layout',
        pathParams: z.object({ board: DashboardBoardSchema }),
        body: c.noBody(),
        responses: { 204: c.noBody() },
        summary: 'BR-DSH-00-09: back to the delivered default (the row is deleted)',
    },
    financial: {
        method: 'GET',
        path: '/api/dashboards/financial',
        responses: { 200: FinancialDashboardSchema, 403: MessageSchema },
        summary: 'DSH-01 — the fixed home page (reports.view + accounting.view)',
    },
    sales: {
        method: 'GET',
        path: '/api/dashboards/sales',
        query: DateRangeQuery.extend({ salespersonId: z.uuid().optional() }),
        responses: { 200: SalesWidgetsSchema, 403: MessageSchema },
        summary: 'DSH-02 widget cluster (reports.view + sales.view)',
    },
    inventory: {
        method: 'GET',
        path: '/api/dashboards/inventory',
        /**
         * `categoryId` is DSH-03 §5a's Category filter. It names ONE category;
         * the widening to that category's whole subtree (BR-DSH-03-22) is the
         * server's, in `DashboardsService.inventory()` — the wire never carries
         * the descendant list.
         */
        query: z.object({
            warehouseId: z.uuid().optional(),
            categoryId: z.uuid().optional(),
            days: z.coerce.number().int().min(1).max(365).optional(),
        }),
        responses: { 200: InventoryWidgetsSchema, 403: MessageSchema },
        summary: 'DSH-03 widget cluster (reports.view + inventory.view)',
    },
    cashflow: {
        method: 'GET',
        path: '/api/dashboards/cashflow',
        query: DateRangeQuery.extend({ currency: CurrencyFilterSchema }),
        responses: { 200: CashflowWidgetsSchema, 403: MessageSchema },
        summary: 'DSH-04 widget cluster (reports.view + banking.view)',
    },
    cashflowForecast: {
        method: 'GET',
        path: '/api/dashboards/cashflow/forecast',
        /**
         * BR-DSH-04-14 — the forecast starts from the current cash balance "for
         * the selected accounts AND CURRENCY", so the filter has to reach it too
         * or day 0 stops being the figure the KPI card above it shows.
         */
        query: z.object({ currency: CurrencyFilterSchema }),
        responses: { 200: CashflowForecastSchema, 403: MessageSchema },
        summary: 'DSH-04 §5e — 30 days from due dates (reports.view + banking.view)',
    },
});
//# sourceMappingURL=dashboards.js.map